/* global __BUILD_LAST_COMMIT__ */
import React, { useEffect, useState } from 'react';

const PROFILE_URL = 'https://github.com/konlet';
const BUILD_LAST_COMMIT = __BUILD_LAST_COMMIT__;

function formatTimeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (Math.abs(diffSec) < 45) return 'just now';

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'always' });
  const units = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60]
  ];

  for (const [unit, size] of units) {
    if (Math.abs(diffSec) >= size) {
      return rtf.format(-Math.round(diffSec / size), unit);
    }
  }
  return 'just now';
}

function isNewer(next, current) {
  if (!next || !next.committedAt) return false;
  if (!current || !current.committedAt) return true;
  return Date.parse(next.committedAt) > Date.parse(current.committedAt);
}

function mergeCommit(current, data) {
  const newer = isNewer(data, current) ? data : current;
  const commitsLast90Days = typeof data.commitsLast90Days === 'number'
    ? data.commitsLast90Days
    : (current && current.commitsLast90Days);
  if (!newer && typeof commitsLast90Days !== 'number') return current;
  return { ...(newer || {}), commitsLast90Days };
}

function formatCommitCount(count) {
  return `${count.toLocaleString('en-US')}`;
}

export default function LastCommit() {
  const [commit, setCommit] = useState(BUILD_LAST_COMMIT);
  const [label, setLabel] = useState(() => (
    BUILD_LAST_COMMIT ? formatTimeAgo(BUILD_LAST_COMMIT.committedAt) : ''
  ));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/api/last-commit');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setCommit((current) => mergeCommit(current, data));
        }
      } catch {
        // Keep the build-time fallback when the API is unavailable.
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!commit || !commit.committedAt) return undefined;

    const update = () => setLabel(formatTimeAgo(commit.committedAt));
    update();
    const id = window.setInterval(update, 30000);
    return () => window.clearInterval(id);
  }, [commit]);

  const countLabel = typeof commit?.commitsLast90Days === 'number'
    ? formatCommitCount(commit.commitsLast90Days)
    : '';
  const showLastCommit = Boolean(commit && commit.committedAt && label);

  if (!showLastCommit && !countLabel) return null;

  const ariaLabel = [
    showLastCommit ? `Last GitHub commit ${label}` : '',
    countLabel ? `Commits (90d): ${countLabel}` : ''
  ].filter(Boolean).join('. ');

  return (
    <p className="last-commit">
      <a
        href={PROFILE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.11.82-.26.82-.577 0-.285-.01-1.04-.016-2.04-3.338.726-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.84 1.237 1.84 1.237 1.07 1.834 2.807 1.304 3.492.997.108-.775.418-1.305.76-1.605-2.665-.304-5.467-1.332-5.467-5.93 0-1.31.468-2.382 1.236-3.22-.124-.304-.536-1.524.117-3.176 0 0 1.008-.322 3.3 1.23a11.5 11.5 0 0 1 3.003-.404c1.02.005 2.047.138 3.006.404 2.29-1.552 3.296-1.23 3.296-1.23.655 1.652.243 2.872.12 3.176.77.838 1.235 1.91 1.235 3.22 0 4.61-2.807 5.624-5.48 5.921.43.372.823 1.102.823 2.222 0 1.606-.015 2.898-.015 3.293 0 .32.218.694.825.576C20.565 21.796 24 17.297 24 12 24 5.37 18.627 0 12 0z"
          />
        </svg>
        <span className="last-commit-text">
          {showLastCommit ? (
            <span className="last-commit-stat">
              <span className="last-commit-label">Last commit:</span>
              {' '}
              <time dateTime={commit.committedAt}>{label}</time>
            </span>
          ) : null}
          {countLabel ? (
            <span className="last-commit-stat">
              <span className="last-commit-label">Commits (prev 90d):</span>
              {' '}
              <span className="last-commit-count">{countLabel}</span>
            </span>
          ) : null}
        </span>
      </a>
    </p>
  );
}
